<script setup lang="ts">
import type { SearchResult } from "~/types/contentSearch"

const {
  result,
  isFree = false,
  template = "article",
} = defineProps<{
  result: SearchResult
  isFree: boolean
  template: "article" | "video"
}>()

const showImageField = computed(() => {
  return template === "video" || result.article.img
})
</script>

<template>
  <div
    class="px-4 flex flex-col md:px-6 py-4 md:py-5 truncate text-wrap w-full"
  >
    <div class="flex gap-3 mb-4 md:mb-3">
      <UContentFreeLabel v-if="isFree" />
      <slot name="badge" />
    </div>

    <div class="flex flex-col-reverse gap-1" :class="{ 'text-white': template === 'video' }">
      <h4 class="font-national2condensed font-medium text-xl/[24px] md:text-2xl/[28px]" v-html="result.title" />
      <h5 class="font-national2 text-sm md:text-base/[24px]" :class="{ 'text-[--theme-dark]': template === 'article' }">
        <span v-if="result.folder">{{ result.folder.priority }}. {{ result.folder.title }} / </span>
        <span class="uppercase">{{ result.subject.priority }}. {{ result.subject.title }}</span>
      </h5>
    </div>

    <p v-if="result.hasAccess">
      <URichText
        class="text-sm md:text-base/[24px] mt-2 md:mt-4"
        :html="result.description"
      />
    </p>
    <UPreviewPaywall v-else class="mt-4 md:mt-6" :title="result.title" :dark="template === 'video'" />
  </div>

  <UIcon
    v-if="!showImageField && !result.hasAccess" name="lock-filled"
    size="lg"
    class="absolute bottom-4 md:bottom-auto md:top-4 right-4 text-darker"
  />
  <div
    v-else-if="showImageField"
    class="md:flex md:flex-shrink-0 md:w-44 h-36 md:h-auto relative rounded-b-2xl md:rounded-l-none md:rounded-r-2xl"
    :class="{
        'bg-[--theme-translucent-darkest]': template === 'video',
        'md:h-auto': !result.hasAccess,
      }"
  >
    <img
      v-if="result.article.img"
      :src="result.article.img.src"
      :alt="result.article.img.alt || ''"
      class="object-cover w-full h-full rounded-b-2xl md:rounded-l-none md:rounded-r-2xl"
      :class="{
          'brightness-50': !result.hasAccess,
        }"
    />
    <div
      class="absolute inset-0 flex p-4 align-center"
      :class="{
          'justify-end': template === 'article',
          'justify-between': !result.hasAccess && template === 'video',
          'md:justify-end': result.hasAccess,
        }"
    >
      <slot name="secondaryBadge" />
      <UIcon
        v-if="!result.hasAccess"
        name="lock-filled"
        class="top-[-2.7rem] right-4 absolute md:static"
        :class="{
          'text-white': template === 'video',
          'text-darker': template === 'article',
          'md:text-white': template === 'article' && result.article.img?.src,
        }"
        size="lg"
      />
    </div>
  </div>
</template>
